import type { DashboardComposition } from "./composition";
import type { JsonValue, DataFilterValue, DataFilterOperator } from "@nexus/widget-sdk";

export interface WidgetDataBinding {
  source: string;
  tenantScope?: "dashboard" | "custom";
  params?: Record<string, JsonValue>;
}

export function normalizeDataBinding(binding: WidgetDataBinding): WidgetDataBinding {
  if (!binding.params || !("tenant_scope" in binding.params)) return binding;
  const params = { ...binding.params };
  const legacy = params.tenant_scope;
  delete params.tenant_scope;
  const tenantScope =
    binding.tenantScope ?? (legacy === "dashboard" || legacy === "custom" ? legacy : undefined);
  return { ...binding, ...(tenantScope ? { tenantScope } : {}), params };
}

export interface WidgetInstance {
  instanceId: string;
  widgetId: string;
  scopeOverride?: { timeRange?: DashboardTimeRange };
  config: Record<string, JsonValue>;
  configBindings?: Record<string, string>;
  dataBindings?: Record<string, WidgetDataBinding>;

  /**
   * Tailwind classes applied by the host to the widget wrapper.
   * The widget component does not receive this value.
   * Example: 'col-span-12 xl:col-span-6'
   */
  layoutClassName?: string;
  /** Start a new responsive row without an empty placeholder widget. */
  startNewRow?: boolean;
  /** Rows the widget covers from the `xl` breakpoint (2 or 3); absent is one. */
  rowSpan?: number;
}

export interface DashboardSectionLabel {
  id: string;
  title: string;
  layoutClassName?: string;
}

export interface DashboardSection {
  sectionId: string;
  title: string;
  subtitle?: string;
  width?: number;
  widgets: WidgetInstance[];
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  collapsed?: boolean;
  widgetGridClassName?: string;
  labels?: DashboardSectionLabel[];
  /**
   * One of the section's own widgets, rendered in its header instead of its
   * grid: a compact summary that stays visible when the section is collapsed.
   */
  summaryInstanceId?: string;
  /**
   * The section is the header of its widgets: they render without their own
   * frame and title, and their headline figures move into the section header.
   */
  embedded?: boolean;
  /** Image reference shown before the title (e.g. a tenant logo). */
  image?: string;
}

export interface DashboardDefinition {
  recordId?: string;
  tenantId?: number;
  dashboardId: string;
  title: string;
  description?: string;
  icon?: string;
  version: 1;
  widgets: WidgetInstance[];
  sections?: DashboardSection[];
}

export type DashboardTimeRange =
  | {
      mode: "relative";
      amount: number;
      unit: "minute" | "hour" | "day" | "week" | "calendar-week";
      endAt: "now" | "start-of-today";
      includeCurrentWeek?: boolean;
    }
  | { mode: "absolute"; start: string; end: string }
  | {
      mode: "scheduled";
      amount: number;
      unit: "day" | "month";
      schedule: (
        | { frequency: "weekly"; weekday: number }
        | { frequency: "monthly"; day: number }
        | { frequency: "interval"; everyDays: number; anchorDate: string }
      ) & { time: string };
      cutoff: { daysBefore: number; time: string };
    }
  | { mode: "event-relative"; beforeSeconds: number; afterSeconds: number };

export interface DashboardRefresh {
  intervalSeconds: number;
  onFocus: "always" | "if-stale" | "never";
  schedule?: "interval" | "daily-utc";
  /** UTC HH:MM of the daily refresh; 00:00 when absent. */
  time?: string;
}

export interface DashboardScopeConfig {
  timeRange?: DashboardTimeRange;
  tenants?: "selected" | "all";
  events?: "selected" | "local" | "none";
  protectedEvents?: "route" | "none";
  bindings?: Partial<
    Record<
      "event_id" | "protected_event_id" | "campaign_id" | "tenant_id",
      {
        pathParam?: string;
        queryParam?: string;
      }
    >
  >;
  autoSelectProtectedEvent?: boolean;
  /** Extra URL/default fields; a stored tenant_id "in" value defines dashboard tenant coverage. */
  parameters?: Record<
    string,
    {
      pathParam?: string;
      queryParam?: string;
      value?: DataFilterValue;
      operator?: DataFilterOperator;
    }
  >;
}

export type DashboardKind = "dashboard" | "template";

export interface DashboardRecord {
  id: string;
  kind?: DashboardKind;
  tenant_id: number | null;
  owner_account_id?: string | null;
  name: string;
  description: string | null;
  slug: string;
  /** @deprecated Data sources are host-registered and selected through widget dataBindings. */
  sources: string[];
  status: string;
  state?: "draft" | "saved";
  protected?: boolean;
  audience_grants?: Array<{
    /** `tenant`: the accounts that read that tenant (the key is its id); `scope`: a data scope, never clients. */
    grantee_type: "account" | "profile" | "scope" | "tenant";
    grantee_key: string;
  }>;
  placements?: Array<{
    section_id: string;
    group_id: string | null;
    order: number | null;
  }>;
  access?: { owned: boolean; editable: boolean };
  draft?: { name: string; description: string; config: DashboardRecord["config"] } | null;
  config: {
    refresh?: DashboardRefresh;
    /** Top-bar controls to show. Absent keeps every control; `[]` hides them all. */
    filters?: Array<"tenants" | "time-range" | "collapse-all" | "events">;
    /** Focus mode (full-size widget view). Absent means off; `true` enables it on widgets that do not decide for themselves. */
    focusMode?: boolean;
    showRefreshStatus?: boolean;
    /** Legacy saved flag; full-page presentation is now always available to viewers. */
    presentationMode?: boolean;
    /**
     * How the dashboard's data is kept current. Absent means `"live"`
     * (near-real-time, the standard auto-refresh cadence). `"report"` is for
     * dashboards refreshed rarely (e.g. once a day, such as the war room):
     * the WebUI polls the backend far less often for them.
     */
    refreshMode?: "live" | "report";
    /** Descriptive labels only; never used for access or navigation. */
    metadata?: { tags?: string[] };
    composition?: DashboardComposition;
    scope?: DashboardScopeConfig;
    title?: string;
    icon?: string;
    version?: 1;
    widgets?: WidgetInstance[];
    sections?: DashboardSection[];
  };
  order: number;
  created_at: string;
  updated_at: string;
}

export function toDashboardDefinition(record: DashboardRecord): DashboardDefinition {
  return {
    recordId: record.id,
    tenantId: record.tenant_id ?? undefined,
    dashboardId: record.slug,
    title: record.config.title?.trim() || record.name,
    description: record.description ?? undefined,
    icon: record.config.icon,
    version: record.config.version ?? 1,
    widgets: applyFocusMode(record.config.widgets ?? [], record.config.focusMode),
    sections: record.config.sections,
  };
}

/**
 * Focus mode is off unless the dashboard turns it on. A widget that sets
 * `fullscreenable` itself keeps its own choice either way.
 */
export function applyFocusMode(
  widgets: WidgetInstance[],
  focusMode: boolean | undefined,
): WidgetInstance[] {
  return widgets.map((widget) => {
    const fullscreenable = widget.config.fullscreenable ?? focusMode === true;
    return widget.config.fullscreenable === fullscreenable
      ? widget
      : { ...widget, config: { ...widget.config, fullscreenable } };
  });
}

/** Tags of the dashboard as displayed: the draft's when it has pending changes. */
export function dashboardTags(dashboard: DashboardRecord): string[] {
  return (dashboard.draft?.config ?? dashboard.config).metadata?.tags ?? [];
}

/** Refresh mode as displayed: the draft's when it has pending changes; defaults to "live". */
export function dashboardRefreshMode(dashboard: DashboardRecord): "live" | "report" {
  return (dashboard.draft?.config ?? dashboard.config).refreshMode ?? "live";
}
